import { z } from 'zod';
import programsJson from '@/data/programs.json';
import categoriesJson from '@/data/program-categories.json';
import seoExtrasJson from '@/data/program-seo-extras.json';
import excerptExtrasJson from '@/data/program-excerpt-extras.json';
import imageExtrasJson from '@/data/program-image-extras.json';
import cardImagesJson from '@/data/program-card-images.json';
import faqExtrasJson from '@/data/program-faq-extras.json';
import categoryTestimonialExtrasJson from '@/data/category-testimonial-extras.json';

/**
 * Program catalog + category pages, pulled by scripts/fetch-programs.mjs.
 * Programs come from the WP API (structured ACF); category copy is scraped
 * verbatim from the rendered live pages. Slugs match live URLs.
 */
const faqSchema = z.object({ question: z.string(), answerHtml: z.string() });

const programSchema = z.object({
  id: z.number(),
  slug: z.string(),
  title: z.string(),
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  heroImage: z.string().optional(),
  excerpt: z.string().optional(),
  subheading: z.string().optional(),
  videoUrl: z.string().optional(),
  /** Poster frame for videoUrl (scripts/fetch-program-posters.mjs) — enables the play-on-click facade. */
  heroPoster: z.string().optional(),
  facts: z.object({
    groupSize: z.string().optional(),
    teamSize: z.string().optional(),
    length: z.string().optional(),
    setting: z.string().optional(),
    physicality: z.string().optional(),
    space: z.string().optional(),
  }),
  idealFor: z.string().optional(),
  whatsIncluded: z.string().optional(),
  techRequirements: z.string().optional(),
  html: z.string(),
  gallery: z.array(z.string()).default([]),
  faqs: z.array(faqSchema).default([]),
  testimonials: z.array(z.object({ client: z.string(), html: z.string() })).default([]),
  related: z.array(z.string()).default([]),
  /**
   * Set on programs authored here after the WordPress cutover (YYYY-MM-DD). The
   * live sitemap snapshot never saw them, so this is their sitemap lastmod.
   */
  addedAt: z.string().date().optional(),
});

const categoryTestimonialSchema = z.object({
  quoteHtml: z.string(),
  name: z.string().optional(),
  company: z.string().optional(),
  logo: z.string().optional(),
});

const categorySchema = z.object({
  slug: z.string(),
  title: z.string().optional(),
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  videoUrl: z.string().optional(),
  /** Poster frame for videoUrl (scripts/fetch-category-posters.mjs) — enables the play-on-click facade. */
  heroPoster: z.string().optional(),
  /** Show the category video right under the hero, above the idea list, instead of opening the activity grid. */
  videoFirst: z.boolean().optional(),
  /** Live hero photo for categories whose hero has no video. */
  heroImage: z.string().optional(),
  introHtml: z.string(),
  programSlugs: z.array(z.string()).default([]),
  faqs: z.array(faqSchema).default([]),
  faqHeading: z.string().optional(),
  sections: z
    .array(z.object({ type: z.enum(['h2', 'h3', 'p', 'list']), html: z.string() }))
    .default([]),
  testimonials: z.array(categoryTestimonialSchema).default([]),
});

export type Program = z.infer<typeof programSchema>;

/** The spec facts a planner compares first, in the order program cards show them. */
export const PROGRAM_FACT = { length: 'length', group: 'group', setting: 'setting' } as const;
export type ProgramFactKind = (typeof PROGRAM_FACT)[keyof typeof PROGRAM_FACT];
export interface ProgramFactLine {
  kind: ProgramFactKind;
  label: string;
  value: string;
}

/** "3 - 4 Hours" the way the catalog stores it becomes "3 to 4 hours" the way the copy reads. */
export function programLengthLabel(length: string | undefined): string | undefined {
  if (!length) return undefined;
  return length.replace(/\s*[-–]\s*/g, ' to ').replace(/\bHours?\b/g, (unit) => unit.toLowerCase());
}

/** Length, group size and setting as fact lines for a photo-led program card. */
export function programFactLines(program: Program): ProgramFactLine[] {
  const { facts } = program;
  const lines: ProgramFactLine[] = [];
  const length = programLengthLabel(facts.length);
  if (length) lines.push({ kind: PROGRAM_FACT.length, label: 'Length', value: length });
  if (facts.groupSize) lines.push({ kind: PROGRAM_FACT.group, label: 'Group size', value: `${facts.groupSize} people` });
  if (facts.setting) lines.push({ kind: PROGRAM_FACT.setting, label: 'Setting', value: facts.setting });
  return lines;
}
export type CategoryTestimonial = ProgramCategory['testimonials'][number];
export type ProgramCategory = z.infer<typeof categorySchema>;

/**
 * Meta descriptions for programs WordPress has none for. Without these the page
 * falls back to live's Yoast text (`live-seo.json`), which for these programs is
 * the entire excerpt — 230+ characters of it.
 *
 * These only FILL A GAP: a program that has its own `seoDescription` keeps it,
 * so an entry here stops applying by itself once WordPress carries the copy and
 * can then be deleted. The override lives here rather than in programs.json
 * because scripts/fetch-programs.mjs regenerates that file wholesale.
 */
const seoExtras = seoExtrasJson as Record<string, { seoDescription?: string } | undefined>;

/**
 * Card excerpts that replace the scraped one, for a program whose excerpt
 * repeats another program's word for word (Virtual Match Me copied the
 * in-person card), so two cards in one grid never read the same. Here rather
 * than in programs.json for the same reason as seoExtras.
 */
const excerptExtras = excerptExtrasJson as Record<string, string | undefined>;

/**
 * Card / hero images for programs whose WordPress image is a logo or a
 * 385px thumbnail (Colors, Emotional Intelligence, Corporate Canvas — the
 * three that looked broken on the hub pages, 2026-09-03). These REPLACE the
 * scraped image: the video poster, a gallery photo, or the category poster.
 * Same reasoning as seoExtras for living outside programs.json.
 */
const imageExtras = z
  .record(
    z.string(),
    z.object({
      heroImage: z.string().optional(),
      /**
       * A real event photo as the video's cover, replacing the frame pulled from
       * the video (a blue title card on some programs, with the play button
       * sitting on the program name). Set per program, never by the template.
       */
      heroPoster: z.string().optional(),
      /** Filename stem of the gallery photo that opens in the frame (see getProgramFullGallery). */
      galleryLead: z.string().optional(),
      /** Filename stem of a gallery image moved to the end (a logo graphic, not a photo). */
      galleryLast: z.string().optional(),
    }),
  )
  .parse(imageExtrasJson);

/** The editorial gallery order for a program, when it has one (program-image-extras.json). */
export function programGalleryOrder(slug: string): { lead?: string | undefined; last?: string | undefined } {
  const extras = imageExtras[slug];
  return { lead: extras?.galleryLead, last: extras?.galleryLast };
}
/**
 * The same photo as the program's hero, at the full size WordPress uploaded it.
 * Many heroes point at a 385x270 thumbnail of a 1000x700 original; a card is
 * ~350 CSS px wide, so on a 2x screen the thumbnail is soft. Cards read the
 * original and the image optimizer makes the card-sized variants. The program
 * page itself keeps its hero.
 */
const cardImages = cardImagesJson as Record<string, string | undefined>;

/** The image a program card shows: the larger original when we have it, else the hero. */
export function programCardImage(program: Program): string | undefined {
  return cardImages[program.slug] ?? program.heroImage;
}

/**
 * Editorial FAQs APPENDED after a program's scraped ones — the answers to
 * the questions buyers actually ask (competitors' pages ask them; ours did
 * not), written from the program's own copy. Same reasoning as seoExtras
 * for living outside programs.json.
 */
const faqExtras = z.record(z.string(), z.array(faqSchema)).parse(faqExtrasJson);

/**
 * 34 programs' "What's Included" opens "Pricing varies depending upon the
 * number of participants (minimum of 30) and includes …" — a sales sentence
 * from the WordPress sidebar. We do not publish pricing, and the list of
 * what is included is the part buyers read, so the preamble comes off.
 */
const PRICING_PREAMBLE_RE = /^Pricing (?:varies|starts at)[\s\S]*?\band includes\s+/i;

function withoutPricingPreamble(text: string): string {
  return text.replace(PRICING_PREAMBLE_RE, 'Includes ');
}

export const PROGRAMS: readonly Program[] = z
  .array(programSchema)
  .parse(programsJson)
  .map((program) => {
    const fallback = seoExtras[program.slug]?.seoDescription;
    const excerpt = excerptExtras[program.slug];
    const image = imageExtras[program.slug]?.heroImage;
    const poster = imageExtras[program.slug]?.heroPoster;
    const extraFaqs = faqExtras[program.slug] ?? [];
    const whatsIncluded = program.whatsIncluded ? withoutPricingPreamble(program.whatsIncluded) : undefined;
    return {
      ...program,
      ...(whatsIncluded !== undefined ? { whatsIncluded } : {}),
      ...(!program.seoDescription && fallback ? { seoDescription: fallback } : {}),
      ...(excerpt ? { excerpt } : {}),
      ...(image ? { heroImage: image } : {}),
      ...(poster ? { heroPoster: poster } : {}),
      ...(extraFaqs.length > 0 ? { faqs: [...program.faqs, ...extraFaqs] } : {}),
    };
  });
/**
 * Editorial client quotes for a category page, REPLACING the scraped set —
 * the home for quote changes sales asks for (add one, reorder, badge with a
 * logo, trim a stale detail like the eight-year-old date in the Lowe's
 * quote). program-categories.json holds what the ACF `client_comments`
 * relationship carried at cutover and fetch-programs.mjs rewrites it
 * wholesale, so edits belong here rather than in that file. Keyed by category
 * slug; a category with no entry keeps its scraped quotes.
 */
const categoryTestimonialExtras = z
  .record(z.string(), z.array(categoryTestimonialSchema))
  .parse(categoryTestimonialExtrasJson);

export const PROGRAM_CATEGORIES: readonly ProgramCategory[] = z
  .array(categorySchema)
  .parse(categoriesJson)
  .map((category) => {
    const override = categoryTestimonialExtras[category.slug];
    return override ? { ...category, testimonials: override } : category;
  });

const bySlug = new Map(PROGRAMS.map((program) => [program.slug, program]));

/** Titles compared without marks, case or spacing: "Crack the Case!®" matches "crack the case". */
const comparableTitle = (text: string) => text.toLowerCase().replace(/[®™!]/g, '').replace(/\s+/g, ' ').trim();

/** What may follow a program name at the start of a line: the end, a colon, a space or an opening bracket. */
const NAME_BOUNDARY = /^($|[:\s(®™!])/;

/**
 * The catalog program a line of copy starts with ("Crack the Case!®: mystery
 * kits…", "Mavericks Murder Mystery (in person)"), and how many characters of
 * the line its name takes. The longest title wins, so "Virtual Murder Mystery:
 * Mavericks on the Case" beats a shorter title it contains.
 */
export function programNamedAtStart(text: string): { program: Program; length: number } | undefined {
  let best: { program: Program; length: number } | undefined;
  for (const program of PROGRAMS) {
    const title = comparableTitle(program.title);
    if (!title) continue;
    // Walk the line until its comparable form covers the title, so the length is in the original text.
    for (let end = 1; end <= text.length; end += 1) {
      const head = comparableTitle(text.slice(0, end));
      if (head.length < title.length) continue;
      if (head === title && NAME_BOUNDARY.test(text.slice(end)) && (!best || end > best.length)) {
        // Keep trailing marks (®, !) with the name.
        let length = end;
        while (/[®™!]/.test(text.charAt(length))) length += 1;
        best = { program, length };
      }
      break;
    }
  }
  return best;
}

export function getProgram(slug: string): Program | undefined {
  return bySlug.get(slug);
}

export function getCategory(slug: string): ProgramCategory | undefined {
  return PROGRAM_CATEGORIES.find((category) => category.slug === slug);
}

/** Splits multi-line ACF text blocks (What's Included etc.) into items. */
export function textBlockItems(block: string | undefined): string[] {
  return (block ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** True for the "Virtual X" program variants (matches the live tabs). */
export function isVirtualProgram(program: Program): boolean {
  return /\bvirtual(ly)?\b/i.test(program.title);
}

/** Plain-text excerpt from a program's description HTML. */
export function programExcerpt(program: Program, max = 200): string {
  const text = program.html
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&#8217;|&rsquo;/g, '’')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max).replace(/\s\S*$/, '')}…` : text;
}

/**
 * The full first paragraph of a program's description — what the live site's
 * program-grid cards display (WP uses the untruncated excerpt there). Matching
 * it keeps card text at content parity on category/listing pages.
 */
export function programCardText(program: Program): string {
  if (program.excerpt && program.excerpt.length > 40) return program.excerpt;
  const firstPara = program.html.match(/<p[^>]*>([\s\S]*?)<\/p>/)?.[1] ?? program.html;
  const text = firstPara
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#8217;|&rsquo;/g, '’')
    .replace(/&#8211;|&ndash;/g, '–')
    .replace(/&#8220;|&ldquo;/g, '“')
    .replace(/&#8221;|&rdquo;/g, '”')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 40 ? text : programExcerpt(program, 400);
}

/** Longest one-line hook a card should carry before it becomes a paragraph. */
const HOOK_MAX_CHARS = 150;

/**
 * Below this, the opening sentence is a slogan ("Embark on a Locate and
 * Donate SmartHunt®!") that says nothing about the program, so the hook
 * takes the next sentence too.
 */
const HOOK_MIN_CHARS = 60;

/**
 * The opening of a program's card text, capped for cards on hub and landing
 * pages, where the program pages themselves hold the full copy: the first
 * sentence, plus the next while the hook is still shorter than a real
 * description. A hook longer than the cap is cut at a word boundary with an
 * ellipsis.
 */
export function programHook(program: Program, maxChars = HOOK_MAX_CHARS): string {
  const text = programCardText(program);
  let hook = firstSentence(text, program.title);
  while (hook.length < HOOK_MIN_CHARS && hook.length < text.length) {
    const rest = text.slice(hook.length).replace(/^\s+/, '');
    if (!rest) break;
    hook = `${hook} ${firstSentence(rest, program.title)}`;
  }
  if (hook.length <= maxChars) return hook;
  return `${hook.slice(0, maxChars).replace(/\s+\S*$/, '')}…`;
}

const SENTENCE_END = /[.!?](?=\s|$)/;

/**
 * The first sentence of `text`, treating punctuation inside the program's own
 * name as part of the name — "Trivia Blast Live! transforms trivia…" is one
 * sentence, not a hook that stops at the title.
 */
function firstSentence(text: string, title: string): string {
  const name = title.replace(/[®™]/g, '').trim();
  let from = 0;
  for (;;) {
    const offset = text.slice(from).search(SENTENCE_END);
    if (offset === -1) return text;
    const end = from + offset + 1;
    const nameAt = name.length > 0 ? text.indexOf(name, from) : -1;
    const endsInsideName = nameAt !== -1 && nameAt < end && end <= nameAt + name.length;
    if (!endsInsideName) return text.slice(0, end);
    from = nameAt + name.length;
  }
}
